'use client'

import { useEffect, useState } from 'react'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getTriage,
    deleteTriage,
    Triage,
    URGENCY_LABELS,
    URGENCY_COLORS,
} from '@/lib/api/triages/triage-api'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowBack,
    MdLocalHospital,
    MdEdit,
    MdDelete,
    MdPerson,
    MdCalendarToday,
    MdMedicalServices,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'

export default function TriageDetailPage() {
    const navigate = useNavigate()
    const { canUpdate, canDelete } = useResourcePermissions('bookings', 'triage')
    const { id } = useParams<{ id: string }>()
    const [triage, setTriage] = useState<Triage | null>(null)
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    useEffect(() => {
        if (id) {
            loadTriage()
        }
    }, [id])

    const loadTriage = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getTriage(parseInt(id, 10))
            if (response.success && response.result) {
                setTriage(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar triagem')
                navigate('/bookings/triage')
            }
        } catch (error) {
            toast.error('Erro ao carregar triagem')
            navigate('/bookings/triage')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = () => {
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!id || !triage) return

        setDeleting(true)
        try {
            const response = await deleteTriage(parseInt(id, 10))
            if (response.success) {
                toast.success('Triagem excluída com sucesso!')
                navigate('/bookings/triage')
            } else {
                toast.error(response.error || 'Erro ao excluir triagem')
            }
        } catch (error) {
            toast.error('Erro ao excluir triagem')
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes da triagem...
                </p>
            </div>
        )
    }

    if (!triage) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdLocalHospital className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Triagem não encontrada
                    </h3>
                    <p className="text-sm text-base-content/60">
                        A triagem que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/bookings/triage')}
                    className="btn btn-secondary"
                >
                    Voltar para Triagens
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/bookings/triage')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex lg:flex rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                            <MdLocalHospital className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                Triagem #{triage.id}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Detalhes da triagem
                            </p>
                        </div>
                    </div>
                </div>
                {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-2">
                        {canUpdate && (
                            <button
                                onClick={() =>
                                    navigate(`/bookings/triage/edit/${triage.id}`)
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdEdit className="text-lg" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="btn btn-error btn-outline gap-2"
                            >
                                {deleting ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdDelete className="text-lg" />
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span
                                className={`w-4 h-4 rounded-full ${URGENCY_COLORS[triage.urgency]
                                    }`}
                            ></span>
                            <span className="font-bold text-lg">
                                {URGENCY_LABELS[triage.urgency]}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                {format(
                                    new Date(triage.triageAt),
                                    "dd 'de' MMMM 'de' yyyy 'as' HH:mm",
                                    { locale: ptBR }
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {triage.client && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdPerson className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Paciente
                                    </p>
                                    <p className="font-semibold">
                                        {triage.client.name}
                                    </p>
                                </div>
                            </div>
                        )}

                        {triage.professional && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Profissional
                                    </p>
                                    <p className="font-semibold">
                                        {triage.professional.name}
                                    </p>
                                    {triage.professional.registrationNumber && (
                                        <p className="text-sm text-base-content/60">
                                            {
                                                triage.professional
                                                    .registrationNumber
                                            }
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {triage.chiefComplaint && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Queixa Principal
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content">
                                    {triage.chiefComplaint}
                                </p>
                            </div>
                        </div>
                    )}

                    {triage.symptoms && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Sintomas
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content">
                                    {triage.symptoms}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <h3 className="text-lg font-bold text-base-content">
                        Sinais Vitais
                    </h3>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {(triage.bloodPressureSystolic ||
                            triage.bloodPressureDiastolic) && (
                                <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Pressão Arterial
                                    </p>
                                    <p className="text-xl font-bold text-secondary">
                                        {triage.bloodPressureSystolic || '-'}/
                                        {triage.bloodPressureDiastolic || '-'}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        mmHg
                                    </p>
                                </div>
                            )}

                        {triage.heartRate && (
                            <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Freq. Cardíaca
                                </p>
                                <p className="text-xl font-bold text-secondary">
                                    {triage.heartRate}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    bpm
                                </p>
                            </div>
                        )}

                        {triage.respiratoryRate && (
                            <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Freq. Respiratória
                                </p>
                                <p className="text-xl font-bold text-secondary">
                                    {triage.respiratoryRate}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    rpm
                                </p>
                            </div>
                        )}

                        {triage.temperature && (
                            <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Temperatura (C)
                                </p>
                                <p className="text-xl font-bold text-secondary">
                                    {triage.temperature}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    C
                                </p>
                            </div>
                        )}

                        {triage.oxygenSaturation && (
                            <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Saturação O2
                                </p>
                                <p className="text-xl font-bold text-secondary">
                                    {triage.oxygenSaturation}%
                                </p>
                            </div>
                        )}

                        {triage.painLevel !== undefined &&
                            triage.painLevel !== null && (
                                <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Nível de Dor
                                    </p>
                                    <p className="text-xl font-bold text-secondary">
                                        {triage.painLevel}/10
                                    </p>
                                </div>
                            )}

                        {triage.weight && (
                            <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Peso
                                </p>
                                <p className="text-xl font-bold text-secondary">
                                    {triage.weight}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    kg
                                </p>
                            </div>
                        )}

                        {triage.height && (
                            <div className="p-4 bg-base-200/50 rounded-xl text-center">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Altura
                                </p>
                                <p className="text-xl font-bold text-secondary">
                                    {triage.height}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    cm
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(triage.allergies ||
                triage.medications ||
                triage.medicalHistory) && (
                    <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                            <h3 className="text-lg font-bold text-base-content">
                                Histórico Médico
                            </h3>
                        </div>

                        <div className="p-6 space-y-4">
                            {triage.allergies && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-2">
                                        Alergias
                                    </h4>
                                    <div className="p-4 bg-error/10 rounded-xl border border-error/20">
                                        <p className="text-base-content">
                                            {triage.allergies}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {triage.medications && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-2">
                                        Medicamentos em Uso
                                    </h4>
                                    <div className="p-4 bg-base-200/50 rounded-xl">
                                        <p className="text-base-content">
                                            {triage.medications}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {triage.medicalHistory && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-2">
                                        Histórico
                                    </h4>
                                    <div className="p-4 bg-base-200/50 rounded-xl">
                                        <p className="text-base-content">
                                            {triage.medicalHistory}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            {triage.observations && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <h3 className="text-lg font-bold text-base-content">
                            Observações
                        </h3>
                    </div>

                    <div className="p-6">
                        <p className="text-base-content whitespace-pre-wrap">
                            {triage.observations}
                        </p>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Excluir triagem"
                message="Tem certeza que deseja excluir esta triagem?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
